import { IconBranch } from "@/assets/icons/geist/IconBranch";
import { IconGitHub } from "@/assets/icons/geist/IconGitHub";
import { TableCell, TableRow } from "@/components/podkit/tables/Table";
import { formatRepoUrlForDisplay } from "@/format/url";
import { useGroupProjects } from "@/hooks/use-group-projects";
import type { PlainProject } from "@/queries/project-queries";
import type { FC } from "react";
import { CollapsibleSection } from "./CollapsibleSection";

export type ProjectsSectionProps = {
    groupId: string;
};

export const ProjectsSection: FC<ProjectsSectionProps> = ({ groupId }) => {
    const { projects, isLoading } = useGroupProjects(groupId);

    const title = `${projects.length} ${projects.length === 1 ? "project" : "projects"}`;
    const headers = ["Name", "Git repo", "Branch"];

    return (
        <CollapsibleSection
            title={title}
            headers={headers}
            isLoading={isLoading}
            isEmpty={projects.length === 0}
        >
            {projects.map((project) => (
                <ProjectTableRow key={project.id} project={project} />
            ))}
        </CollapsibleSection>
    );
};

type ProjectTableRowProps = {
    project: PlainProject;
};

const ProjectTableRow: FC<ProjectTableRowProps> = ({ project }) => {
    const spec = project.initializer?.specs?.[0];
    const gitSpec = spec?.spec.case === "git" ? spec.spec.value : undefined;
    const remoteUri = gitSpec?.remoteUri || "";
    const branch = gitSpec?.cloneTarget || "main";

    return (
        <TableRow>
            <TableCell>{project.metadata?.name || "Unnamed"}</TableCell>
            <TableCell>
                {remoteUri ? (
                    <div className="flex items-center gap-2">
                        <IconGitHub size="sm" className="shrink-0" />
                        <span className="font-mono text-xs text-muted-foreground">
                            {formatRepoUrlForDisplay(remoteUri, true)}
                        </span>
                    </div>
                ) : (
                    <span className="text-muted-foreground">N/A</span>
                )}
            </TableCell>
            <TableCell>
                <div className="flex items-center gap-2">
                    <IconBranch size="sm" className="shrink-0" />
                    <span className="text-muted-foreground">{branch}</span>
                </div>
            </TableCell>
        </TableRow>
    );
};
